import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiHome,
  FiCalendar,
  FiUsers,
  FiSettings,
  FiHelpCircle,
  FiLogOut,
  FiSend,
  FiExternalLink,
} from "react-icons/fi";
import { SHARE_URL } from "../utils/appHost";
import { Badge, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const NAV_ITEMS = [
  { Icon: FiHome, labelKey: "admin_sidebar.home", path: "/admin", exact: true },
  { Icon: FiCalendar, labelKey: "admin_sidebar.my_events", path: "/admin/events" },
  { Icon: FiUsers, labelKey: "admin_sidebar.social_feed", path: "/social-events" },
  // Pic-Loud Share lives on its own subdomain: full page navigation
  { Icon: FiSend, labelKey: "admin_sidebar.file_transfers", path: `${SHARE_URL}/`, external: true },
];

const BOTTOM_ITEMS = [
  { Icon: FiSettings, labelKey: "admin_sidebar.settings", path: "/admin/settings" },
  { Icon: FiHelpCircle, labelKey: "admin_sidebar.support", path: "/support" },
];

export default function AdminSidebar({ user, onLogout, supportUnread = 0, socialUnread = { total: 0 } }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isActive = (path, exact) =>
    exact ? pathname === path : pathname.startsWith(path);

  const badgeFor = (path) => {
    if (path === "/support") return supportUnread;
    if (path === "/social-events") return socialUnread?.total || 0;
    return 0;
  };

  const renderItem = ({ Icon, labelKey, path, exact, external }) => {
    const active = !external && isActive(path, exact);
    const badge = badgeFor(path);
    const LinkTag = external ? "a" : Link;
    return (
      <LinkTag
        key={path}
        {...(external ? { href: path } : { to: path })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 12px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          color: active ? "#6366f1" : "#374151",
          background: active ? "#eef2ff" : "transparent",
          textDecoration: "none",
          marginBottom: 2,
          transition: "background 0.15s, color 0.15s",
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.background = "#f9fafb";
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.background = "transparent";
        }}
      >
        <Icon size={16} style={{ flexShrink: 0 }} aria-hidden="true" />
        <span style={{ flex: 1 }}>{t(labelKey)}</span>
        {external && <FiExternalLink size={12} style={{ color: "#9ca3af" }} aria-hidden="true" />}
        {badge > 0 && (
          <Badge
            bg="danger"
            pill
            style={{
              fontSize: "0.6rem",
              minWidth: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
            }}
          >
            {badge}
          </Badge>
        )}
      </LinkTag>
    );
  };

  return (
    <div
      style={{
        width: 220,
        flexShrink: 0,
        background: "#fff",
        borderRight: "1px solid #f0f0f0",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "sticky",
        top: 0,
        overflowY: "auto",
      }}
    >
      {/* Logo */}
      <div style={{ padding: "16px 20px 12px" }}>
        <Link to="/admin">
          <Image
            src={`${process.env.REACT_APP_MODEL_ENDPOINT}/user_logo?user_id=${user?.user_id}&t=${Date.now()}`}
            alt="Logo"
            height="32"
            style={{ borderRadius: "6px", objectFit: "contain" }}
          />
        </Link>
      </div>

      <div style={{ height: 1, background: "#f0f0f0", margin: "0 16px 8px" }} />

      {/* Main nav */}
      <nav style={{ padding: "0 10px", flex: 1 }}>
        {NAV_ITEMS.map(renderItem)}
      </nav>

      {/* Bottom nav + logout */}
      <div style={{ padding: "8px 10px", borderTop: "1px solid #f0f0f0" }}>
        {BOTTOM_ITEMS.map(renderItem)}

        <button
          onClick={onLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            color: "#ef4444",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <FiLogOut size={16} style={{ flexShrink: 0 }} aria-hidden="true" />
          <span>{t("admin_sidebar.logout")}</span>
        </button>
      </div>
    </div>
  );
}
